import { useEffect, useMemo } from 'react'

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CadastroImobiliariaCorretorForm } from '@/features/proposta/components/cadastro-imobiliaria-corretor-form'
import { CardEtapaContent, CardEtapaItem } from '@/features/proposta/components/ui/card-etapa'
import { CadastroImobiliariaCorretorEntity } from '@/features/proposta/entities/cadastro-imobiliaria-corretor.entity'
import { useCorretagens } from '@/features/proposta/hooks/use-corretagens'
import type { TPropostaLocationState } from '@/features/proposta/interfaces/proposta.type'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import { Render } from '@/shared/components/render-if'
import { Label } from '@/shared/components/ui'
import { FormField, FormItem } from '@/shared/components/ui/form'
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group'
import { useTypedLocation } from '@/shared/hooks/use-typed-location'
import { AlertDialog } from '@/shared/lib/alert-dialog'

type TImobiliariaCorretorProps = {
  exibeFormaPagamentoComissao?: boolean
  percentualCorretagem: number | null
  valorCorretagem: number | null
}
const ImobiliariaCorretorStep = ({
  percentualCorretagem,
  valorCorretagem,
  exibeFormaPagamentoComissao,
}: TImobiliariaCorretorProps) => {
  const { t } = useTranslation('proposta')
  const { state } = useTypedLocation<TPropostaLocationState>()
  const { data } = useCorretagens(state.simulacoesIds)
  const { control, watch, setValue, resetField, formState, getFieldState } = useFormContext<TPropostaSchemaInput>()

  const cadastroImobiliariaCorretorEntity = useMemo(
    () => new CadastroImobiliariaCorretorEntity(data ?? [], percentualCorretagem, valorCorretagem),
    [data, percentualCorretagem, valorCorretagem],
  )
  const nameParticipacoes = 'detalhesDaProposta.imobiliariasCorretores.participacoes'
  const { isDirty: isParticipacoesDirty, invalid: isParticipacoesInvalid } = getFieldState(nameParticipacoes, formState)
  const isSemParticipacoes = watch('detalhesDaProposta.imobiliariasCorretores.incluirParticipacoes') === 'Não'

  const isStepCompleted = isSemParticipacoes || (!isParticipacoesInvalid && isParticipacoesDirty)

  const handleRequestConfirmacaoSelecionarNao = async () =>
    await AlertDialog.show({
      title: t('detalhes_proposta.imobiliaria.dialog.cancelar_preenchimento.titulo'),
      description: t('detalhes_proposta.imobiliaria.dialog.cancelar_preenchimento.descricao'),
      actions: [
        {
          title: t('detalhes_proposta.imobiliaria.dialog.cancelar_preenchimento.voltar'),
          value: 'cancel',
          variant: 'outline',
        },
        {
          title: t('detalhes_proposta.imobiliaria.dialog.cancelar_preenchimento.confirmar'),
          value: 'confirm',
          variant: 'default',
        },
      ],
    })

  const labelNao = t('detalhes_proposta.imobiliaria.inputs.radio.nao')
  const labelSim = t('detalhes_proposta.imobiliaria.inputs.radio.sim')
  const isAdicionarNovaParticipacaoDesabilitado =
    watch('detalhesDaProposta.imobiliariasCorretores.participacoes')?.filter((p) => !p.imobiliariaCorretor.idExterno)
      ?.length > 0

  const isParticipacoesVazias = watch(nameParticipacoes)?.length === 0
  useEffect(() => {
    if (!isSemParticipacoes && isParticipacoesVazias) {
      setValue('detalhesDaProposta.imobiliariasCorretores.incluirParticipacoes', 'Não')
    }
  }, [isParticipacoesVazias])

  return (
    <CardEtapaItem
      titulo={t('detalhes_proposta.imobiliaria.titulo')}
      descricao={t('detalhes_proposta.imobiliaria.descricao')}
      completed={isStepCompleted}
    >
      <CardEtapaContent>
        <span className="text-sm font-medium text-neutral-800">
          {t('detalhes_proposta.imobiliaria.inputs.radio.label')}
        </span>
        <FormField
          control={control}
          name="detalhesDaProposta.imobiliariasCorretores.incluirParticipacoes"
          render={({ field }) => (
            <FormItem>
              <RadioGroup
                className="flex flex-col gap-6"
                value={field.value}
                onValueChange={async (v) => {
                  if (v === labelSim) {
                    field.onChange(v)
                    setValue(nameParticipacoes, [cadastroImobiliariaCorretorEntity.novoCadastro])
                    return
                  }
                  if (!isParticipacoesDirty) {
                    field.onChange(v)
                    return
                  }
                  const result = await handleRequestConfirmacaoSelecionarNao()
                  if (result.value === 'confirm') {
                    resetField('detalhesDaProposta.imobiliariasCorretores')
                    field.onChange(v)
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={labelSim} value={labelSim} />
                  <Label htmlFor={labelSim} aria-labelledby={labelSim} className="font-nunito text-base font-normal">
                    {labelSim}
                  </Label>
                </div>
                <Render.If condition={field.value === labelSim}>
                  {() => (
                    <CadastroImobiliariaCorretorForm
                      control={control}
                      exibeFormaPagamentoComissao={exibeFormaPagamentoComissao}
                      cadastroImobiliariaCorretoresEntity={cadastroImobiliariaCorretorEntity}
                      formaPagamentoComissao={watch('detalhesDaProposta.imobiliariasCorretores.formaPagamentoComissao')}
                      isAdicionarNovaParticipacaoDesabilitado={isAdicionarNovaParticipacaoDesabilitado}
                    />
                  )}
                </Render.If>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={labelNao} value={labelNao} />
                  <Label aria-labelledby={labelNao} className="font-nunito text-base font-normal" htmlFor={labelNao}>
                    {labelNao}
                  </Label>
                </div>
              </RadioGroup>
            </FormItem>
          )}
        />
      </CardEtapaContent>
    </CardEtapaItem>
  )
}

export { ImobiliariaCorretorStep }
